import {
  BILLING_ENVIRONMENTS,
  FEATURE_KEYS,
  FEATURE_LABELS,
  LIMIT_KEYS,
  LIMIT_LABELS,
  type AdminPlanDto,
  type PlanLimits,
  type StripePriceIds,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { FormField } from "@/components/form-field";
import { PageHeader } from "@/components/page-header";
import { ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { errorMessage } from "@/lib/api";
import { billingApi, billingQueries } from "@/lib/billing-api";

export const Route = createFileRoute("/admin/planos")({
  component: AdminPlansPage,
});

function AdminPlansPage() {
  const plans = useQuery(billingQueries.adminPlans());
  return (
    <>
      <PageHeader
        title="Planos"
        description="Preços, limites e recursos valem na hora para todos os assinantes do plano. No Stripe, preço novo = Price novo: cadastre o id aqui."
      />
      {plans.isPending ? (
        <LoadingState rows={3} />
      ) : plans.isError ? (
        <ErrorState error={plans.error} onRetry={() => void plans.refetch()} />
      ) : (
        <div className="space-y-6">
          {plans.data.map((p) => (
            <PlanForm key={`${p.id}-${p.priceMonthlyCents}-${p.priceYearlyCents}`} plan={p} />
          ))}
        </div>
      )}
    </>
  );
}

function reais(cents: number): string {
  return (cents / 100).toFixed(2).replace(".", ",");
}
function cents(value: string): number {
  const n = Number(value.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

function PlanForm({ plan }: { plan: AdminPlanDto }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(plan.name);
  const [description, setDescription] = useState(plan.description);
  const [monthly, setMonthly] = useState(reais(plan.priceMonthlyCents));
  const [yearly, setYearly] = useState(reais(plan.priceYearlyCents));
  const [limits, setLimits] = useState<PlanLimits>(plan.limits);
  const [features, setFeatures] = useState(plan.features);
  const [active, setActive] = useState(plan.active);
  const [prices, setPrices] = useState<StripePriceIds>(plan.stripePriceIds);
  const [confirm, setConfirm] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      billingApi.updatePlan(plan.id, {
        name,
        description,
        priceMonthlyCents: cents(monthly),
        priceYearlyCents: cents(yearly),
        limits,
        features,
        active,
        stripePriceIds: prices,
      }),
    onSuccess: async () => {
      setConfirm(false);
      toast.success(`Plano ${name} salvo.`);
      await queryClient.invalidateQueries({ queryKey: ["admin"] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const setPrice = (env: (typeof BILLING_ENVIRONMENTS)[number], field: "monthly" | "yearly", value: string) => {
    const current = prices[env] ?? { monthly: null, yearly: null };
    setPrices({ ...prices, [env]: { ...current, [field]: value.trim() ? value.trim() : null } });
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle>{plan.name}</CardTitle>
          <Badge variant="outline">{plan.code}</Badge>
          <Badge variant={active ? "success" : "secondary"}>{active ? "Ativo" : "Inativo"}</Badge>
          <span className="text-sm text-muted-foreground">{plan.subscriberCount} workspace(s)</span>
        </div>
        <CardDescription>Plano inativo some do checkout; quem já assina continua nele.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField id={`${plan.id}-nome`} label="Nome">
            <Input id={`${plan.id}-nome`} value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField id={`${plan.id}-mensal`} label="Preço mensal (R$)">
              <Input id={`${plan.id}-mensal`} inputMode="decimal" value={monthly} onChange={(e) => setMonthly(e.target.value)} />
            </FormField>
            <FormField id={`${plan.id}-anual`} label="Preço anual (R$)">
              <Input id={`${plan.id}-anual`} inputMode="decimal" value={yearly} onChange={(e) => setYearly(e.target.value)} />
            </FormField>
          </div>
        </div>
        <FormField id={`${plan.id}-desc`} label="Descrição">
          <Textarea id={`${plan.id}-desc`} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </FormField>
        <div>
          <p className="mb-2 text-sm font-medium">Limites (vazio = ilimitado)</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {LIMIT_KEYS.map((k) => (
              <FormField key={k} id={`${plan.id}-${k}`} label={LIMIT_LABELS[k]}>
                <Input
                  id={`${plan.id}-${k}`}
                  inputMode="numeric"
                  placeholder="Ilimitado"
                  value={limits[k] === null ? "" : String(limits[k])}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    setLimits({ ...limits, [k]: v === "" ? null : Number(v) });
                  }}
                />
              </FormField>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          {FEATURE_KEYS.map((f) => (
            <label key={f} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={features[f]} onChange={(e) => setFeatures({ ...features, [f]: e.target.checked })} />
              {FEATURE_LABELS[f]}
            </label>
          ))}
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Disponível para assinar
          </label>
        </div>
        <div>
          <p className="mb-2 text-sm font-medium">Ids de Price do Stripe por ambiente</p>
          <div className="grid gap-3 lg:grid-cols-3">
            {BILLING_ENVIRONMENTS.map((env) => (
              <div key={env} className="space-y-2 rounded-lg border p-3">
                <p className="text-xs font-medium text-muted-foreground">{env}</p>
                <Input aria-label={`${env} mensal`} placeholder="price_... (mensal)" value={prices[env]?.monthly ?? ""} onChange={(e) => setPrice(env, "monthly", e.target.value)} />
                <Input aria-label={`${env} anual`} placeholder="price_... (anual)" value={prices[env]?.yearly ?? ""} onChange={(e) => setPrice(env, "yearly", e.target.value)} />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Button onClick={() => setConfirm(true)}>Salvar plano</Button>
      </CardFooter>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={`Salvar o plano ${name}?`}
        description={`Os novos limites valem na hora para os ${plan.subscriberCount} workspace(s) deste plano. Mudança de preço só afeta o Stripe se você cadastrar o Price novo.`}
        confirmLabel="Salvar"
        loading={save.isPending}
        onConfirm={() => save.mutate()}
      />
    </Card>
  );
}
